import { ChevronLeft, File, Folder, LoaderCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { AttachmentRef, WorkspaceListing } from '../api';
import { formatBytes } from '../utils';

interface WorkspaceBrowserProps {
  open: boolean;
  load(path: string): Promise<WorkspaceListing>;
  onClose(): void;
  onAttach(attachment: AttachmentRef): void;
}

export function WorkspaceBrowser({ open, load, onClose, onAttach }: WorkspaceBrowserProps) {
  const [path, setPath] = useState('');
  const [listing, setListing] = useState<WorkspaceListing>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    load(path)
      .then((result) => {
        if (!cancelled) setListing(result);
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(errorMessage(reason));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, open, path]);

  useEffect(() => {
    if (!open) {
      setPath('');
      setListing(undefined);
      setError(undefined);
    }
  }, [open]);

  if (!open) return null;

  const parent = listing?.parent ?? parentPath(path);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="workspace-browser"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workspace-browser-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="browser-header">
          <div>
            <h2 id="workspace-browser-title">浏览工作区</h2>
            <p>选择文件作为本轮研究材料，不会直接打开本机路径。</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭工作区浏览器">
            <X size={18} />
          </button>
        </header>

        <div className="browser-pathbar">
          <button
            className="icon-button"
            type="button"
            disabled={!path}
            onClick={() => setPath(parent)}
            aria-label="返回上一级"
          >
            <ChevronLeft size={18} />
          </button>
          <code>{listing?.path || path || '工作区根目录'}</code>
        </div>

        <div className="browser-content">
          {loading && (
            <div className="panel-state">
              <LoaderCircle className="spin" size={20} />
              <span>正在读取目录</span>
            </div>
          )}
          {!loading && error && <div className="panel-state error-message">{error}</div>}
          {!loading && !error && listing?.entries.length === 0 && (
            <div className="panel-state">当前目录为空。</div>
          )}
          {!loading && !error && listing && (
            <div className="file-list">
              {listing.entries.map((entry) => (
                <button
                  type="button"
                  className="file-row"
                  key={entry.path}
                  onClick={() => {
                    if (entry.kind === 'directory') {
                      setPath(entry.path);
                    } else {
                      onAttach({
                        name: entry.name,
                        path: entry.path,
                        kind: 'workspace',
                        size: entry.size,
                        mimeType: entry.mimeType,
                        raw: entry.raw,
                      });
                    }
                  }}
                >
                  <span className="file-kind" aria-hidden="true">
                    {entry.kind === 'directory' ? <Folder size={17} /> : <File size={17} />}
                  </span>
                  <span className="file-copy">
                    <strong>{entry.name}</strong>
                    <small>{entry.kind === 'directory' ? '文件夹' : entry.mimeType || '文件'}</small>
                  </span>
                  {entry.size !== undefined && <span className="file-size">{formatBytes(entry.size)}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function parentPath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
}

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : '工作区目录读取失败。';
}
